import { Injectable } from '@angular/core';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { Recurrence, RecurrenceLogMapped } from '@core/interfaces';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment-timezone';
import { CreateRecurrenceLinkLogsService } from './create-recurrence-link-logs.service';
import { UpdatedByLogService } from './updated-by-log.service';

@Injectable({
  providedIn: 'root'
})
export class CreateRecurrenceLogsService {
  public recurrenceLogsMapped: RecurrenceLogMapped[] = [];

  constructor(
    private updatedByLog: UpdatedByLogService,
    private translateService: TranslateService,
    private authenticationService: AuthenticationService,
    private createRecurrenceLinkLogsService: CreateRecurrenceLinkLogsService
  ) { }

  public listRecurrenceLogsMapped(recurrence: Recurrence): RecurrenceLogMapped[] {
    this.recurrenceLogsMapped = [];

    recurrence.logs.forEach((currentLog, index) => {
      const previousLog = recurrence.logs[index - 1];
      const log = {
        ...currentLog,
        icon: 'ic_plus_20px',
        eventDescription: null,
        eventName: this.translateService.instant('RECURRENCES.DIALOG.TABS.HISTORIC.EVENTS.CREATED'),
        colorClass: 'light-purple',
        formattedCreatedAt: moment(currentLog.createdAt).local().format('DD/MM/YYYY'),
        createdAt: moment(currentLog.createdAt).local().format('DD/MM/YYYY HH:mm:ss'),
        showMessage: false,
        showLinks: false,
      };

      if (!previousLog) {
        return this.generateCreatedLog({ log });
      }

      const hasChangedEquipments = JSON.stringify(currentLog.snapshot.equipments || []) !== JSON.stringify(previousLog.snapshot.equipments || []);
      const hasChangedLocationSectors = JSON.stringify(currentLog.snapshot.locationSectors || []) !== JSON.stringify(previousLog.snapshot.locationSectors || []);
      const hasChangedLocationEnvironments = JSON.stringify(currentLog.snapshot.locationEnvironments || []) !== JSON.stringify(previousLog.snapshot.locationEnvironments || []);
      const hasChangedLinks = hasChangedLocationSectors || hasChangedLocationEnvironments || hasChangedEquipments;

      if (hasChangedLinks) {
        this.generateLinksLog({ log });
      }

      if (currentLog.snapshot.archived !== previousLog.snapshot.archived) {
        this.generateArchiveOrUnarchiveLog({ log });
      }

      if (currentLog.snapshot.message !== previousLog.snapshot.message) {
        this.generateMessageLog({ log });
      }

      if (currentLog.snapshot.location.id !== previousLog.snapshot.location.id) {
        this.generateLocationLog({ log, previousLog });
      }
    });

    const recurrencesLogsWithUpdatedBy = this.updatedByLog.customUpdatedByInLogs(this.recurrenceLogsMapped);
    return recurrencesLogsWithUpdatedBy.reverse();
  }

  private generateCreatedLog({ log }): void {
    this.recurrenceLogsMapped.push({
      ...log,
      isCreatedLog: true,
      showMessage: true,
      showLinks: true,
      links: this.createRecurrenceLinkLogsService.generateLinks(log.snapshot)
    });

  }

  private generateLinksLog({ log }): void {
    log.eventName = this.translateService.instant('RECURRENCES.DIALOG.TABS.HISTORIC.EVENTS.LINKS_UPDATED');
    log.icon = 'ic_links';
    log.colorClass = 'brown';

    const links = this.createRecurrenceLinkLogsService.generateLinks(log.snapshot);
    this.recurrenceLogsMapped.push({
      ...log,
      links,
      showLinks: true,
    });
  }

  private generateArchiveOrUnarchiveLog({ log }): void {
    if (log.snapshot.archived) {
      log.eventName = this.translateService.instant('RECURRENCES.DIALOG.TABS.HISTORIC.EVENTS.ARCHIVED');
      log.icon = 'ic_archive_18px';
      log.colorClass = 'light-gray';
    } else {
      log.eventName = this.translateService.instant('RECURRENCES.DIALOG.TABS.HISTORIC.EVENTS.UNARCHIVED');
      log.icon = 'ic_unarchive_18px';
      log.colorClass = 'dark-gray';
    }

    this.recurrenceLogsMapped.push(log);
  }

  private generateMessageLog({ log }): void {
    log.eventName = this.translateService.instant('RECURRENCES.DIALOG.TABS.HISTORIC.EVENTS.DESCRIPTION_UPDATED');
    log.icon = 'ic_chat_20px';
    log.colorClass = 'gray';

    this.recurrenceLogsMapped.push({
      ...log,
      showMessage: true
    });
  }

  private generateLocationLog({ log, previousLog }): void {
    log.icon = 'ic_location_18px';
    log.colorClass = 'blue';
    log.eventName = this.translateService.instant('RECURRENCES.DIALOG.TABS.HISTORIC.EVENTS.LOCATION_UPDATED');
    log.eventDescription = this.translateService.instant('RECURRENCES.DIALOG.TABS.HISTORIC.EVENTS.LOCATION_DESCRIPTION', {
      previousLocation: previousLog.snapshot.location.name,
      currentLocation: log.snapshot.location.name
    });

    this.recurrenceLogsMapped.push(log);
  }
}
